/**
 * Referencia al prospecto dentro de la demo compartida (blueprint §B.6).
 *
 * EL PROBLEMA QUE RESUELVE
 *
 * La demo es UNA sola instancia atendiendo a muchos prospectos a la vez. Cuando
 * alguien pulsa "simular compra", ¿de quién fue esa acción?
 *
 * Por dirección de red no se puede: cambia entre wifi y datos móviles, y varias
 * personas detrás del mismo NAT comparten una. Por billetera tampoco: en
 * simulacro puede no existir todavía. Y por sesión menos: si el prospecto vuelve
 * mañana desde el portátil, es otra sesión y sigue siendo la misma persona.
 *
 * La respuesta es un token de correlación que nace con el prospecto —cuando pide
 * la demo— y viaja hasta cada evento. Este módulo guarda el extremo de esa
 * cadena en el navegador: el `leadId` que salió de canjear el enlace mágico.
 *
 * Se guarda aparte del estado de la demo (`demo-store`) a propósito: "reiniciar
 * demo" tiene que poder limpiar la simulación —cartera, transacciones, estado de
 * verificación— SIN perder de vista a quién le pertenece la sesión. Si se
 * borrara el prospecto, se perdería toda la telemetría posterior.
 */

const CLAVE = 'platform.demo-lead.v1'

export interface ReferenciaProspecto {
  leadId: string
  /** Nombre que dio en el formulario, para saludarlo por su nombre. */
  name?: string | null
  /** Cuándo caduca su enlace: la demo lo avisa antes de que se quede fuera. */
  expiresAt?: string | null
  /** Cuándo canjeó por primera vez en ESTE navegador. */
  enteredAt: string
}

function leer(): ReferenciaProspecto | null {
  if (typeof window === 'undefined') return null
  try {
    const crudo = window.localStorage.getItem(CLAVE)
    if (!crudo) return null
    const v = JSON.parse(crudo) as ReferenciaProspecto
    return typeof v?.leadId === 'string' && v.leadId ? v : null
  } catch {
    return null
  }
}

let cache: ReferenciaProspecto | null = leer()
const oyentes = new Set<() => void>()

function avisar() {
  for (const o of oyentes) o()
}

/** Sella la correlación tras canjear el enlace. */
export function sellarProspecto(ref: Omit<ReferenciaProspecto, 'enteredAt'>): void {
  const completo: ReferenciaProspecto = { ...ref, enteredAt: new Date().toISOString() }
  cache = completo
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(completo))
  } catch {
    /* modo privado o cuota llena: la sesión sigue funcionando, sin persistir */
  }
  avisar()
}

/** El prospecto actual, o null si entró sin enlace. */
export function prospectoActual(): ReferenciaProspecto | null {
  return cache
}

/** Solo el identificador, que es lo que necesita el emisor de beacons. */
export function leadIdActual(): string | null {
  return cache?.leadId ?? null
}

/**
 * Olvida al prospecto. NO lo llama el botón de reiniciar la demo — solo se usa
 * cuando el enlace caducó y hay que mandarlo a pedir otro.
 */
export function olvidarProspecto(): void {
  cache = null
  try {
    window.localStorage.removeItem(CLAVE)
  } catch {
    /* ignorar */
  }
  avisar()
}

/** ¿El enlace de este prospecto ya venció? */
export function enlaceVencido(ahora: Date = new Date()): boolean {
  const exp = cache?.expiresAt
  if (!exp) return false
  const t = Date.parse(exp)
  return Number.isFinite(t) && t < ahora.getTime()
}

/** Días que le quedan de acceso, redondeado hacia arriba. */
export function diasRestantes(ahora: Date = new Date()): number | null {
  const exp = cache?.expiresAt
  if (!exp) return null
  const t = Date.parse(exp)
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.ceil((t - ahora.getTime()) / (24 * 60 * 60 * 1000)))
}

/** Suscripción para que la interfaz reaccione al canje sin recargar. */
export function suscribirse(oyente: () => void): () => void {
  oyentes.add(oyente)
  return () => {
    oyentes.delete(oyente)
  }
}

export function instantanea(): ReferenciaProspecto | null {
  return cache
}
